import { format, isValid } from 'date-fns';
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { Tr, Td, Button, IconButton, Text, Image, Box } from "@chakra-ui/react";
import { CrossIcon, CheckIcon } from "../../../icons";
import { formatDateOnly, formatDateWithOptionalTime } from "../../../utils/helpers";
import { useState, useEffect } from "react";
import { loadSavedImageByID } from "../../../utils/helpers";
import { useAppContext } from "../../../contexts/AppProvider";

import UserAvatar from "../../UserAvatar";
import { resolveOtherSpecificationField } from "./tableInputColumnMeta";

const mergeEquipmentOtherDisplay = (
  dataValue: any,
  value: string,
  column: { otherSpecificationField?: string }
) => {
  const raw = dataValue[value];
  const otherSpecificationField = resolveOtherSpecificationField(column);
  if (!otherSpecificationField || String(raw) !== "Other") return raw;
  const spec = String(dataValue[otherSpecificationField] ?? "").trim();
  return spec || "Other";
};

const TableInputRow = ({ columns, index, dataValue, openModal, disabled, disableRemoveRow }) => {
  return (
    <Tr>
      {columns?.map((col) => {
        const { value, type, variant } = col;
        if (type === 'peoplePicker') {
          return (
            <Td key={`row-${index}-${value}`} px={2}>
              <UserAvatar userId={dataValue[value]} />
            </Td>
          );
        } else if (type === 'datepicker') {
          switch (variant) {
            case 'date':
              return (
                <Td key={`row-${index}-${value}`} px={2}>
                  {dataValue[value] ? formatDateOnly(dataValue[value]) : ''}
                </Td>
              );

            case 'time':
              return (
                <Td key={`row-${index}-${value}`} px={2}>
                  {dataValue[value] ? (() => {
                    const d = new Date(dataValue[value]);
                    return isValid(d) ? format(d, 'HH:mm') : '';
                  })() : ''}
                </Td>
              );

            default: // datetime
              return (
                <Td key={`row-${index}-${value}`} px={2}>
                  {dataValue[value] ? formatDateWithOptionalTime(dataValue[value]) : ''}
                </Td>
              );
          }
        } else if (type === 'signature') {
          return (
            <Td key={`row-${index}-${value}`} px={2}>
              {dataValue[value] ? <CheckIcon color='breSuccess' w="16px" /> : <CrossIcon color='breFail' w="16px" />}
            </Td>
          );
        } else if (type === 'image') {
          return (
            <ImageCell key={`row-${index}-${value}`} imageData={dataValue[value]} />
          );
        }
        const displayText = mergeEquipmentOtherDisplay(dataValue, value, col);
        return (
          <Td key={`row-${index}-${value}`} px={2}>
            <Text whiteSpace='break-spaces'>{displayText}</Text>
          </Td>
        );
      }
      )}
      {!disabled && (
        <>
          <Td fontWeight="semi_medium" paddingInline="0px">
            <Button
              disabled={disabled}
              _disabled={{ opacity: 0.75, cursor: "not-allowed" }}
              bg="TechSignatureTableRow.button.bg"
              h="30px"
              color="TechSignatureTableRow.button.color"
              pl="5px"
              pr="10px"
              fontSize="ssm"
              borderRadius="10px"
              borderWidth="1px"
              borderColor="TechSignatureTableRow.button.color"
              onClick={() =>
                openModal("edit", {
                  ...dataValue,
                  index: index,
                })
              }
            >
              <EditIcon
                w="12px"
                h="12px"
                ml="5px"
                mr="8px"
                stroke="TechSignatureTableRow.editIcon.stroke"
              />
              Edit
            </Button>
          </Td>
          {!disableRemoveRow && (
          <Td fontWeight="semi_medium" paddingInline="20px">
            <IconButton
              bg="TechSignatureTableRow.deleteIcon.bg"
              aria-label="delete"
              size="xs"
              isRound={true}
              disabled={disabled}
              _disabled={{ opacity: 0.75, cursor: "not-allowed" }}
              icon={
                <DeleteIcon
                  h="15"
                  w="15"
                  stroke="TechSignatureTableRow.deleteIcon.stroke"
                  onClick={() =>
                    openModal("delete", {
                      ...dataValue,
                      index: index,
                    })
                  }
                />
              }
            />
          </Td>
          )}
        </>
      )}
    </Tr>
  );
};

const ImageCell = ({ imageData }) => {
  const [imageUrl, setImageUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const { settings } = useAppContext();

  useEffect(() => {
    if (imageData) {
      // Handle both array and single object formats
      const image = Array.isArray(imageData) ? imageData[0] : imageData;
      if (image && image.fileId) {
        setLoading(true);
        loadSavedImageByID(
          image.fileId,
          settings?.spSiteUrl || "",
          settings?.spDocumentLibrary || "",
          true // thumbnail
        )
          .then((url) => {
            setImageUrl(url);
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      }
    }
  }, [imageData, settings]);

  if (loading) {
    return (
      <Td px={2}>
        <Box w="50px" h="50px" display="flex" alignItems="center" justifyContent="center">
          <Text fontSize="xs">Loading...</Text>
        </Box>
      </Td>
    );
  }

  if (imageUrl) {
    return (
      <Td px={2}>
        <Image src={imageUrl} alt="Component photo" maxW="50px" maxH="50px" objectFit="contain" />
      </Td>
    );
  }

  return (
    <Td px={2}>
      <Text fontSize="xs" color="gray.400">No image</Text>
    </Td>
  );
};

export default TableInputRow;
